import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditEspecie, EditPresentacion, EditCalibre } from './components';
import {
  type EspecieListItem,
  type PresentacionListItem,
  type CalibreListItem,
  type EspecieRequest,
  type PresentacionRequest,
  type CalibreRequest
} from './types/especie';
import { useEspecieStore } from './store';

const EspeciesPage: React.FC = () => {
  const [selectedEspecie, setSelectedEspecie] = React.useState<number | null>(null);
  const [selectedPresentacion, setSelectedPresentacion] = React.useState<number | null>(null);

  // Dialog states
  const [especieDialogOpen, setEspecieDialogOpen] = React.useState(false);
  const [presentacionDialogOpen, setPresentacionDialogOpen] = React.useState(false);
  const [calibreDialogOpen, setCalibreDialogOpen] = React.useState(false);
  const [selectedEspecieId, setSelectedEspecieId] = React.useState<number | null>(null);
  const [selectedPresentacionId, setSelectedPresentacionId] = React.useState<number | null>(null);
  const [selectedCalibreId, setSelectedCalibreId] = React.useState<number | null>(null);

  // Store
  const {
    especies,
    especiesLoading,
    especiesError,
    presentaciones,
    presentacionesLoading,
    presentacionesError,
    calibres,
    calibresLoading,
    calibresError,
    fetchEspecies,
    saveEspecie,
    fetchPresentacionesByEspecie,
    savePresentacion,
    fetchCalibresByPresentacion,
    saveCalibre,
    clearEspeciesError,
    clearPresentacionesError,
    clearCalibresError,
  } = useEspecieStore();

  React.useEffect(() => {
    fetchEspecies();
  }, [fetchEspecies]);

  const especieColumns: GridColDef[] = [
    { field: 'nombreComun', headerName: 'Nombre Común', width: 200, flex: 1 },
    { field: 'nombreCientifico', headerName: 'Nombre Científico', width: 250, flex: 1 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEditEspecie(params.row.idEspecie)}
          ariaLabel={`Editar especie ${params.row.nombreComun}`}
        />
      ),
    },
  ];

  const presentacionColumns: GridColDef[] = [
    { field: 'descripcion', headerName: 'Descripción', width: 250, flex: 1 },
    {
      field: 'esFresco',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Fresco' : 'Procesado'}
          color={params.value ? 'success' : 'info'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEditPresentacion(params.row.idPresentacion)}
          ariaLabel={`Editar presentación ${params.row.descripcion}`}
        />
      ),
    },
  ];

  const calibreColumns: GridColDef[] = [
    { field: 'descripcion', headerName: 'Descripción', width: 300, flex: 1 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEditCalibre(params.row.idCalibre)}
          ariaLabel={`Editar calibre ${params.row.descripcion}`}
        />
      ),
    },
  ];

  const handleEspecieRowClick = (params: any) => {
    const especieId = params.row.idEspecie;
    setSelectedEspecie(especieId);
    setSelectedPresentacion(null);
    fetchPresentacionesByEspecie(especieId);
  };

  const handlePresentacionRowClick = (params: any) => {
    const presentacionId = params.row.idPresentacion;
    setSelectedPresentacion(presentacionId);
    fetchCalibresByPresentacion(presentacionId);
  };

  // Especie handlers
  const handleEditEspecie = (idEspecie: number) => {
    setSelectedEspecieId(idEspecie);
    setEspecieDialogOpen(true);
  };

  const handleCreateEspecie = () => {
    setSelectedEspecieId(null);
    setEspecieDialogOpen(true);
  };

  const handleSaveEspecie = async (especie: EspecieRequest) => {
    await saveEspecie(especie);
    setEspecieDialogOpen(false);
    setSelectedEspecieId(null);
  };

  // Presentacion handlers
  const handleEditPresentacion = (idPresentacion: number) => {
    setSelectedPresentacionId(idPresentacion);
    setPresentacionDialogOpen(true);
  };

  const handleCreatePresentacion = () => {
    if (!selectedEspecie) return;
    setSelectedPresentacionId(null);
    setPresentacionDialogOpen(true);
  };

  const handleSavePresentacion = async (presentacion: PresentacionRequest) => {
    await savePresentacion(presentacion);
    setPresentacionDialogOpen(false);
    setSelectedPresentacionId(null);
  };

  // Calibre handlers
  const handleEditCalibre = (idCalibre: number) => {
    setSelectedCalibreId(idCalibre);
    setCalibreDialogOpen(true);
  };

  const handleCreateCalibre = () => {
    if (!selectedPresentacion) return;
    setSelectedCalibreId(null);
    setCalibreDialogOpen(true);
  };

  const handleSaveCalibre = async (calibre: CalibreRequest) => {
    await saveCalibre(calibre);
    setCalibreDialogOpen(false);
    setSelectedCalibreId(null);
  };

  if (especiesLoading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Especies"
          subtitle="Gestión de especies, presentaciones y calibres"
        />
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </ListLayoutPanel>
    );
  }

  if (especiesError) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Especies"
          subtitle="Gestión de especies, presentaciones y calibres"
        />
        <Alert
          severity="error"
          onClose={clearEspeciesError}
        >
          {especiesError}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Especies"
        subtitle="Gestión de especies, presentaciones y calibres"
      />

      {/* First Row: Especies Grid - Full Width */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Especies
          </Typography>
          <AddButton
            text="Nueva Especie"
            onClick={handleCreateEspecie}
          />
        </Box>
        <DataGrid
          columns={especieColumns}
          rows={especies}
          height={300}
          pagination={false}
          loading={especiesLoading}
          getRowId={(row: EspecieListItem) => row.idEspecie}
          onRowClick={handleEspecieRowClick}
        />
      </Box>

      {/* Second Row: Presentaciones and Calibres side by side */}
      <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
        {/* Presentaciones Grid - Half Width */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Presentaciones
              </Typography>
              <AddButton
                text="Nueva Presentación"
                onClick={handleCreatePresentacion}
                disabled={!selectedEspecie}
              />
            </Box>
            {selectedEspecie && (
              <Typography variant="body2" color="text.secondary">
                Especie: {especies.find(e => e.idEspecie === selectedEspecie)?.nombreComun}
              </Typography>
            )}
          </Box>
          {presentacionesError && (
            <Alert severity="error" onClose={clearPresentacionesError} sx={{ mb: 2 }}>
              {presentacionesError}
            </Alert>
          )}
          <DataGrid
            columns={presentacionColumns}
            rows={presentaciones}
            height={300}
            pagination={false}
            loading={presentacionesLoading}
            getRowId={(row: PresentacionListItem) => row.idPresentacion}
            onRowClick={handlePresentacionRowClick}
          />
        </Box>

        {/* Calibres Grid - Half Width */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Calibres
              </Typography>
              <AddButton
                text="Nuevo Calibre"
                onClick={handleCreateCalibre}
                disabled={!selectedPresentacion}
              />
            </Box>
            {selectedPresentacion && (
              <Typography variant="body2" color="text.secondary">
                Presentación: {presentaciones.find(p => p.idPresentacion === selectedPresentacion)?.descripcion}
              </Typography>
            )}
          </Box>
          {calibresError && (
            <Alert severity="error" onClose={clearCalibresError} sx={{ mb: 2 }}>
              {calibresError}
            </Alert>
          )}
          <DataGrid
            columns={calibreColumns}
            rows={calibres}
            height={300}
            pagination={false}
            loading={calibresLoading}
            getRowId={(row: CalibreListItem) => row.idCalibre}
          />
        </Box>
      </Box>

      {/* Edit Dialogs */}
      <EditEspecie
        open={especieDialogOpen}
        especieId={selectedEspecieId}
        onClose={() => setEspecieDialogOpen(false)}
        onSave={handleSaveEspecie}
        title={selectedEspecieId ? "Editar Especie" : "Nueva Especie"}
      />

      <EditPresentacion
        open={presentacionDialogOpen}
        presentacionId={selectedPresentacionId}
        idEspecie={selectedEspecie || 0}
        onClose={() => setPresentacionDialogOpen(false)}
        onSave={handleSavePresentacion}
        title={selectedPresentacionId ? "Editar Presentación" : "Nueva Presentación"}
      />

      <EditCalibre
        open={calibreDialogOpen}
        calibreId={selectedCalibreId}
        idPresentacion={selectedPresentacion || 0}
        onClose={() => setCalibreDialogOpen(false)}
        onSave={handleSaveCalibre}
        title={selectedCalibreId ? "Editar Calibre" : "Nuevo Calibre"}
      />
    </ListLayoutPanel>
  );
};

export default EspeciesPage;